import { toast } from "sonner";
import { upsertFieldOverride, deleteFieldOverride } from "@/lib/api";
import { TABS } from "@/components/booking-form/registry/tabs";
import { FIELDS } from "@/components/booking-form/registry/fields";

const REGISTRY_TAB_KEYS = new Set(TABS.map((t) => t.key));

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 40);

// Tenant-defined tabs (scope='tab', non-registry key) and section dividers (scope='divider').
export function useCustomItems({ clientId, overrides, setOverrides, effective }) {
  const customTabs = overrides
    .filter((o) => o.scope === "tab" && !REGISTRY_TAB_KEYS.has(o.field_key))
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((o) => ({ key: o.field_key, label: o.label || o.field_key, isCustom: true, _row: o }));

  const dividersForTab = (tabKey) =>
    overrides
      .filter((o) => o.scope === "divider" && effective("divider", o.field_key, "tab_key", o.tab_key) === tabKey)
      .map((o) => ({
        key: o.field_key,
        label: effective("divider", o.field_key, "label", o.label || ""),
        position: effective("divider", o.field_key, "position", o.position ?? 0),
        _row: o,
      }))
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.key.localeCompare(b.key));

  const insertRow = async (payload, successMsg) => {
    const { data, error: err } = await upsertFieldOverride({ client_id: clientId, is_visible: true, ...payload });
    if (err) { toast.error(err.message); return; }
    setOverrides((o) => [...o, data]);
    toast.success(successMsg);
  };

  const removeRow = async (row, successMsg) => {
    const { error: err } = await deleteFieldOverride(row.id);
    if (err) { toast.error(err.message); return; }
    setOverrides((o) => o.filter((x) => x.id !== row.id));
    toast.success(successMsg);
  };

  const addCustomTab = async () => {
    const label = window.prompt('Add a new tab. Display label (e.g. "Compliance Docs"):');
    if (!label || !label.trim()) return;
    const trimmed = label.trim();
    const baseKey = slugify(trimmed) || `tab_${Date.now()}`;
    const taken = [...REGISTRY_TAB_KEYS, ...customTabs.map((c) => c.key)];
    let key = baseKey, n = 1;
    while (taken.includes(key)) { n += 1; key = `${baseKey}_${n}`; }
    await insertRow({ scope: "tab", field_key: key, label: trimmed, position: 1000 + customTabs.length },
      `Added tab "${trimmed}"`);
  };

  const deleteCustomTab = async (tab) => {
    if (overrides.some((o) => o.scope === "field" && o.tab_key === tab.key)) {
      toast.error("Move all fields out of this tab before deleting it.");
      return;
    }
    if (!window.confirm(`Delete custom tab "${tab.label}"? This can't be undone.`)) return;
    await removeRow(tab._row, `Deleted tab "${tab.label}"`);
  };

  const addDivider = async (tabKey) => {
    const label = window.prompt(`Add a section divider to "${tabKey}". Heading text:`);
    if (!label || !label.trim()) return;
    const maxFieldPos = FIELDS
      .filter((f) => effective("field", f.key, "tab_key", f.tab) === tabKey)
      .reduce((m, f) => Math.max(m, effective("field", f.key, "position", f.position) ?? 0), 0);
    const maxDivPos = dividersForTab(tabKey).reduce((m, d) => Math.max(m, d.position ?? 0), 0);
    await insertRow({
      scope: "divider", field_key: `div_${Date.now().toString(36)}`, label: label.trim(),
      tab_key: tabKey, position: Math.max(maxFieldPos, maxDivPos) + 10,
    }, `Added divider "${label.trim()}"`);
  };

  const deleteDivider = async (d) => {
    if (!window.confirm(`Delete divider "${d.label || d.key}"?`)) return;
    await removeRow(d._row, "Deleted divider");
  };

  return { customTabs, dividersForTab, addCustomTab, deleteCustomTab, addDivider, deleteDivider };
}
